/**
 * Status feedback copy: the request-state sentences (loading, offline,
 * conflict, refused), the not-available page and the toasts.
 */

export interface StatusFeedbackCopy {
  readonly states: {
    readonly loading: string;
    readonly offlineTitle: string;
    readonly offlineBody: string;
    readonly failedTitle: string;
    readonly failedBody: string;
    readonly conflictTitle: string;
    readonly conflictBody: string;
    readonly saveFailedTitle: string;
    readonly saveFailedBody: string;
    readonly forbiddenTitle: string;
    readonly rateLimited: string;
    readonly sessionExpired: string;
    readonly reload: string;
  };

  readonly denied: {
    readonly title: string;
    readonly body: string;
    readonly toDashboard: string;
  };

  readonly toasts: {
    readonly claimed: (reference: string) => string;
    readonly submitted: (reference: string) => string;
    readonly approved: (reference: string) => string;
    readonly rejected: (reference: string) => string;
    readonly completed: (reference: string) => string;
    readonly returned: (reference: string) => string;
    readonly proposed: string;
    readonly referenceAdded: string;
    readonly onboardingDone: string;
  };
}

export const STATUS_FEEDBACK_COPY_EN: StatusFeedbackCopy = {
  states: {
    loading: 'Loading…',
    offlineTitle: 'You appear to be offline',
    offlineBody: 'Nothing was changed. Check your connection and try again.',
    failedTitle: 'Something went wrong',
    failedBody: 'The request could not be completed. Try again in a moment.',
    conflictTitle: 'This task changed since you opened it',
    conflictBody:
      'Somebody else acted on it first. Review the current state before trying again.',
    saveFailedTitle: 'Your change was not saved',
    saveFailedBody: 'What you typed is still here. Try again when you are ready.',
    forbiddenTitle: 'Not permitted',
    rateLimited: 'Too many changes in a short period. Wait a moment and try again.',
    sessionExpired: 'Your session expired',
    reload: 'Reload',
  },

  denied: {
    title: 'This page is not available',
    body: 'The page does not exist, or your role does not include it.',
    toDashboard: 'Go to the dashboard',
  },

  toasts: {
    claimed: (reference) => `${reference} claimed. It is now In progress.`,
    submitted: (reference) => `${reference} submitted for review.`,
    approved: (reference) => `${reference} approved into Ready.`,
    rejected: (reference) => `${reference} rejected.`,
    completed: (reference) => `${reference} approved to Done.`,
    returned: (reference) => `${reference} returned to In progress.`,
    proposed: 'Your proposal is in the Backlog.',
    referenceAdded: 'Reference added to the task.',
    onboardingDone: 'Welcome aboard. Your dashboard is ready.',
  },
};

export const STATUS_FEEDBACK_COPY_AR: StatusFeedbackCopy = {
  states: {
    loading: 'جارٍ التحميل…',
    offlineTitle: 'يبدو أنك غير متصل',
    offlineBody: 'لم يتغيّر شيء. تحقق من اتصالك وحاول مجددًا.',
    failedTitle: 'حدث خطأ ما',
    failedBody: 'تعذّر إكمال الطلب. حاول بعد لحظات.',
    conflictTitle: 'تغيّرت هذه المهمة منذ فتحتها',
    conflictBody: 'سبقك شخص آخر إليها. راجع الحالة الحالية قبل المحاولة مجددًا.',
    saveFailedTitle: 'لم يُحفظ تغييرك',
    saveFailedBody: 'ما كتبته ما يزال هنا. حاول مجددًا متى شئت.',
    forbiddenTitle: 'غير مسموح',
    rateLimited: 'تغييرات كثيرة في وقت قصير. انتظر لحظة وحاول مجددًا.',
    sessionExpired: 'انتهت جلستك',
    reload: 'إعادة التحميل',
  },

  denied: {
    title: 'هذه الصفحة غير متاحة',
    body: 'الصفحة غير موجودة، أو أن دورك لا يشملها.',
    toDashboard: 'الانتقال إلى لوحة العمل',
  },

  toasts: {
    claimed: (reference) => `استُلمت ${reference}. أصبحت قيد التنفيذ.`,
    submitted: (reference) => `أُرسلت ${reference} للمراجعة.`,
    approved: (reference) => `اعتُمدت ${reference} إلى جاهزة.`,
    rejected: (reference) => `رُفضت ${reference}.`,
    completed: (reference) => `اعتُمدت ${reference} كمنجزة.`,
    returned: (reference) => `أُعيدت ${reference} إلى قيد التنفيذ.`,
    proposed: 'اقتراحك الآن في المقترحات.',
    referenceAdded: 'أُضيف المرجع إلى المهمة.',
    onboardingDone: 'أهلًا بك. لوحة عملك جاهزة.',
  },
};
